"use client";

import { AnimatePresence, motion } from "framer-motion";
import CountryFlag from "react-country-flag";
import { InfiniteData } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import ScoreCard from "../ScoreCard/ScoreCard";
import LeaderboardLoading from "./LeaderboardLoading";
import { groupInnerVariants } from "./animations";
import { ReactNode, useEffect, useState, JSX } from "react";
import clsx from "clsx";

interface TournamentPlayerData {
  id: string;
  playerId: string;
  playername: string;
  gender: string;
  nationality: string;
  category: string;
  picture: string;
  hcp: string;
  total: {
    pos: number;
    thru: number;
    state: string;
    toPar: number;
    score: number;
  };
  latestRound: { scorecardId: string; roundNumber: number; toPar: number };
  rounds: [
    {
      scorecardId: string;
      state: string;
      roundNumber: number;
      score: number;
      roundState: string;
    },
  ];
}

interface LeaderboardData {
  data: {
    node: {
      leaderboard: {
        records: {
          items: [TournamentPlayerData];
        };
      };
    };
  };
}

interface StrokeplayLeaderboardProps {
  gender?: string;
  data?: InfiniteData<AxiosResponse<LeaderboardData, any>>;
  favoritePlayers?: [TournamentPlayerData];
  isLoading: boolean;
  ErrorMessage: () => JSX.Element | null;
  favouritesList: string[];
  setFavouritesList: (favourites: string[]) => void;
  refetchFavourites: () => void;
  tournamentName?: string;
  isMultiRoundTournament?: boolean;
}

const PlayerDataTable = ({
  children,
  isFavoriteLeaderboard,
  ErrorMessage,
  isLoading,
  isMultiRoundTournament,
}: {
  children: ReactNode;
  isFavoriteLeaderboard?: boolean;
  ErrorMessage: () => JSX.Element | null;
  isLoading: boolean;
  isMultiRoundTournament?: boolean;
}) => {
  return (
    <div
      role="table"
      className="bg-[#2c2c2c] rounded-[10px] overflow-hidden min-h-[800px]"
      style={{
        marginBottom: isFavoriteLeaderboard ? "6rem" : 0,
        minHeight: isFavoriteLeaderboard ? 0 : "800px",
      }}
    >
      <div
        role="thead"
        className="flex justify-between bg-tm-c-gray-700 gap-[5px] md:gap-2.5
        px-2.5 py-0 lg:p-0
        [&>div]:py-[15px] [&>div]:px-0 [&>div]:flex-1 [&>div]:text-center [&>div]:text-xs lg:[&>div]:text-[15px]"
      >
        <div role="th">Fav</div>
        <div role="th">Pos</div>
        <div role="th">
          <span className="hidden lg:block">Country</span>
        </div>
        <div
          role="th"
          className="!flex-[5] max-w-[130px] !text-left min-[461px]:max-w-[160px] min-[561px]:max-w-[180px] min-[621px]:max-w-[220px] min-[761px]:max-w-none"
        >
          Player
        </div>
        <div role="th">Score</div>
        <div role="th">Thru</div>
        {isMultiRoundTournament && (
          <>
            <div role="th">Round</div>
            <div role="th">R1</div>
            <div role="th">R2</div>
          </>
        )}
        <div role="th" className="pr-[15px]">
          Total
        </div>
      </div>
      <div role="tbody">
        <ErrorMessage />
        {children}
        {isLoading && (
          <LeaderboardLoading
            columns={7}
            loadingLayout="strokeplay"
            rows={isFavoriteLeaderboard ? 5 : 25}
          />
        )}
      </div>
    </div>
  );
};

const PlayerDataRow = ({
  player,
  isPlayerInSameGroup,
  pageIndex,
  playerIndex,
  gender,
  tournamentName,
  favouritesList,
  setFavouritesList,
  isMultiRoundTournament,
}: {
  player: TournamentPlayerData;
  isPlayerInSameGroup: boolean;
  pageIndex: number;
  playerIndex: number;
  gender?: string;
  tournamentName?: string;
  favouritesList: string[];
  setFavouritesList: (favourites: string[]) => void;
  isMultiRoundTournament?: boolean;
}) => {
  const [isRowOpen, setIsRowOpen] = useState(false);

  return (
    <div
      role="tr"
      className={clsx(
        "group-hover cursor-pointer transition-colors duration-300 lg:hover:bg-[#171717]",
        {
          "opacity-20":
            gender &&
            player.gender &&
            player.gender.toLowerCase() !== gender?.toLowerCase(),
          "border-t border-t-tm-c-gray-700": !isPlayerInSameGroup,
        },
      )}
      key={player.id}
    >
      <div
        role="tr"
        className={clsx(
          `flex justify-between w-full gap-[5px] md:gap-2.5 
          py-0 px-2.5 lg:p-0
          [&>div]:py-[15px] [&>div]:px-0 [&>div]:flex-1 [&>div]:text-center [&>div]:text-xs lg:[&>div]:text-[15px]
        `,
          {
            "bg-tm-c-gray-900": isRowOpen,
          },
        )}
        onClick={() => {
          window.dataLayer = window.dataLayer || [];
          if (!isRowOpen && player.rounds[0]?.scorecardId) {
            window.dataLayer.push({
              event: "openScorecard",
              scorecardId: player.rounds[0].scorecardId,
              playerId: player.playerId,
              tournamentName: tournamentName || "",
              playerState: player.rounds[0].state,
            });
            setIsRowOpen(true);
          }
          if (isRowOpen) {
            setIsRowOpen(false);
          }
        }}
      >
        <div role="td">
          <span
            onClick={(e) => {
              e.stopPropagation();
              const findFavourite = favouritesList.find(
                (i: string) => i === player.playerId,
              );
              if (findFavourite) {
                const removeFavourite = favouritesList.filter(
                  (i: string) => i !== player.playerId,
                );
                setFavouritesList(removeFavourite);
                localStorage.setItem(
                  "leaderboard_favourites",
                  JSON.stringify(removeFavourite),
                );
              } else {
                const addFavourite = [...favouritesList, player.playerId];
                setFavouritesList(addFavourite);
                localStorage.setItem(
                  "leaderboard_favourites",
                  JSON.stringify(addFavourite),
                );
              }
            }}
          >
            {favouritesList.find((i: string) => i === player.playerId) ? (
              <StarIcon />
            ) : (
              <StarBorderIcon />
            )}
          </span>
        </div>
        <div role="td">
          {player.total.state.toLowerCase() === "did_not_finish" ||
          player.total.state.toLowerCase() === "no_show"
            ? "DNF"
            : !isPlayerInSameGroup || (pageIndex === 0 && playerIndex === 0)
              ? player.total.pos
              : ""}
        </div>
        <div role="td" className="flex-1 justify-center text-center">
          {player.nationality && (
            <CountryFlag
              countryCode={player.nationality}
              className="text-base lg:text-xl"
              svg
            />
          )}
        </div>
        <div
          role="td"
          className="relative !flex-[5] max-w-[130px] !text-left min-[461px]:max-w-[160px] min-[561px]:max-w-[180px] min-[621px]:max-w-[220px] min-[761px]:max-w-none overflow-hidden lg:overflow-auto whitespace-nowrap lg:whitespace-normal"
        >
          <span>{player.playername}</span>
          {player.category === "AMATEUR" && (
            <span className="opacity-50 ml-3">(A)</span>
          )}
          <span
            className={`min-[761px]:hidden min-[761px]:group-hover:hidden w-[40px]
              bg-gradient-to-r from-[rgba(255,255,255,0)] to-[#2c2c2c]
              block absolute top-0 bottom-0
              left-[90px] min-[461px]:left-[120px] min-[561px]:left-[140px] min-[621px]:left-[180px]
            `}
          />
        </div>
        <div role="td" className="flex-1">
          {player?.total?.toPar === null && (
            <span className="inline-block w-[35px] rounded-[3px] lg:w-[45px]">
              -
            </span>
          )}
          {player?.total?.toPar < 0 && (
            <span className="inline-block bg-tm-c-brand w-[35px] rounded-[3px] lg:w-[45px]">
              {player.total.toPar}
            </span>
          )}
          {player?.total?.toPar == 0 && (
            <span className="inline-block bg-[#8e8e8e] w-[35px] rounded-[3px] lg:w-[45px]">
              E
            </span>
          )}
          {player?.total?.toPar > 0 && (
            <span className="inline-block bg-tm-c-dark w-[35px] rounded-[3px] lg:w-[45px]">
              +{player.total.toPar}
            </span>
          )}
        </div>
        <div role="td">
          {player.total.thru === 18 ? "F" : player.total.thru}
          {isMultiRoundTournament && (
            <span
              className={clsx({
                "opacity-60": player?.latestRound?.roundNumber,
              })}
            >
              {player?.latestRound?.roundNumber
                ? `(R${player?.latestRound?.roundNumber})`
                : "-"}
            </span>
          )}
        </div>
        {isMultiRoundTournament && (
          <>
            <div role="td">
              {!player?.latestRound && "-"}
              {!!player?.latestRound &&
                (player?.latestRound?.toPar?.toString().startsWith("-")
                  ? player?.latestRound?.toPar
                  : player?.latestRound?.toPar
                    ? `+${player?.latestRound?.toPar}`
                    : "E")}
            </div>
            <div role="td">
              {player?.rounds?.find((r) => r.roundNumber == 1)?.score || "-"}
            </div>
            <div role="td">
              {player?.rounds?.find((r) => r.roundNumber == 2)?.score || "-"}
            </div>
          </>
        )}
        <div role="td" className="pr-[15px]">
          {player.total?.score || "-"}
        </div>
      </div>
      <motion.div
        role="tr"
        className={clsx("overflow-hidden cursor-default", {
          "bg-tm-c-gray-900": isRowOpen,
        })}
        variants={groupInnerVariants}
        initial="initial"
        animate={isRowOpen ? "open" : "initial"}
        exit="initial"
      >
        <AnimatePresence>
          {isRowOpen && (
            <ScoreCard
              isNineHoles={
                player.total.state.toLowerCase() === "played" &&
                player.total.thru === 9
              }
              fetchData={!!player.latestRound?.scorecardId && isRowOpen}
              shouldIntervalRefetch={player.total.thru < 18}
              playerId={player.playerId}
              playerScoreCardId={player.latestRound?.scorecardId}
              latestRound={player.latestRound?.roundNumber}
              multiRoundScoreCardIds={player.rounds.map(
                ({ roundNumber, scorecardId }) => ({
                  roundNumber,
                  scorecardId,
                }),
              )}
              profilePicture={player.picture}
              playerName={player.playername}
              playerHcp={player.hcp}
              playerGender={player.gender}
              isOpen={isRowOpen}
              isMultiRoundTournament={isMultiRoundTournament}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

const StrokeplayLeaderboard = ({
  data,
  gender,
  isLoading,
  ErrorMessage,
  favoritePlayers,
  favouritesList,
  setFavouritesList,
  refetchFavourites,
  tournamentName,
  isMultiRoundTournament,
}: StrokeplayLeaderboardProps) => {
  const [isFirstRender, setIsFirstRender] = useState(true);
  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
    if (!!favouritesList.length) {
      refetchFavourites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoritePlayers]);

  useEffect(() => {
    const getFavourites = localStorage.getItem("leaderboard_favourites");
    if (getFavourites) {
      setFavouritesList(JSON.parse(getFavourites));
    } else {
      localStorage.setItem("leaderboard_favourites", JSON.stringify([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!isFirstRender && !!favoritePlayers?.length && (
        <PlayerDataTable
          isFavoriteLeaderboard
          ErrorMessage={ErrorMessage}
          isLoading={isLoading}
        >
          {favoritePlayers?.map(
            (player: TournamentPlayerData, playerIndex: number) => {
              return (
                <PlayerDataRow
                  key={player.playerId}
                  player={player}
                  pageIndex={0}
                  playerIndex={playerIndex}
                  isPlayerInSameGroup
                  gender={gender}
                  tournamentName={tournamentName}
                  favouritesList={favouritesList}
                  setFavouritesList={setFavouritesList}
                />
              );
            },
          )}
        </PlayerDataTable>
      )}
      <PlayerDataTable
        ErrorMessage={ErrorMessage}
        isLoading={isLoading}
        isMultiRoundTournament={isMultiRoundTournament}
      >
        {data?.pages?.map(
          (page: { data: LeaderboardData }, pageIndex: number) => {
            if (
              pageIndex === 0 &&
              !page?.data?.data?.node?.leaderboard?.records?.items.length
            ) {
              return (
                <h2
                  key={pageIndex}
                  className="text-tm-c-dark opacity-60 text-[25px] mt-[160px] mx-10 mb-0 text-center"
                >
                  No results found
                </h2>
              );
            }

            return page?.data?.data?.node?.leaderboard?.records?.items?.map(
              (player: TournamentPlayerData, playerIndex: number) => {
                const currentPos = player.total.pos;

                const prevPlayerInPage =
                  page.data.data.node.leaderboard.records.items[
                    playerIndex - 1
                  ];
                const prevPosInPage = prevPlayerInPage?.total?.pos;

                // Last player of previous page is needed to determine if current player is in the same position group in pagination
                const prevPageLastPos =
                  pageIndex > 0
                    ? data?.pages?.[
                        pageIndex - 1
                      ]?.data?.data?.node?.leaderboard?.records?.items?.at(-1)
                        ?.total?.pos
                    : null;

                // Is this player part of an existing tie group?
                const isPlayerInSameGroup =
                  // first player of a new page but tied with last of previous page
                  (playerIndex === 0 && prevPageLastPos === currentPos) ||
                  // tied with previous player in the same page
                  prevPosInPage === currentPos;

                return (
                  <PlayerDataRow
                    key={player.playerId}
                    player={player}
                    pageIndex={pageIndex}
                    playerIndex={playerIndex}
                    isPlayerInSameGroup={isPlayerInSameGroup}
                    gender={gender}
                    tournamentName={tournamentName}
                    favouritesList={favouritesList}
                    setFavouritesList={setFavouritesList}
                    isMultiRoundTournament={isMultiRoundTournament}
                  />
                );
              },
            );
          },
        )}
      </PlayerDataTable>
    </>
  );
};

export default StrokeplayLeaderboard;
